import { useEffect, useRef, useState } from 'react'
import Icon from './Icon'
import { UploadError, uploadPoster } from '../lib/storage'

/**
 * Drag-and-drop image upload.
 *
 * Three ways in, because people reach for different ones: drop a file, click to
 * browse, or paste from the clipboard — the last matters more than it sounds
 * when the artwork arrives in a Slack message.
 *
 * A local preview appears the moment a file is chosen, before the upload
 * finishes, so the dialog confirms the right image immediately rather than
 * after a round trip.
 */
export default function ImageDrop({
  value,
  onChange,
  onBusyChange,
  label = 'Tour poster',
  hint = 'Portrait artwork looks best. JPG, PNG or WebP, up to 10MB.',
}: {
  value?: string | null
  onChange: (url: string | null) => void
  /** Lets the parent block Save while bytes are still going up. */
  onBusyChange?: (busy: boolean) => void
  label?: string
  hint?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const cancelRef = useRef<(() => void) | null>(null)
  const previewRef = useRef<string | null>(null)

  const [dragging, setDragging] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  // Object URLs are a leak if they aren't revoked; this releases the previous
  // one whenever it's replaced and the last one on unmount.
  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current)
    }
  }, [])

  function setLocalPreview(file: File | null) {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current)
    previewRef.current = file ? URL.createObjectURL(file) : null
    setPreview(previewRef.current)
  }

  async function accept(file: File | null | undefined) {
    if (!file) return
    setError(null)
    setLocalPreview(file)
    setProgress(0)
    onBusyChange?.(true)

    try {
      const handle = uploadPoster(file, setProgress)
      cancelRef.current = handle.cancel
      const url = await handle.done
      onChange(url)
    } catch (caught) {
      setError(
        caught instanceof UploadError ? caught.message : (caught as Error).message,
      )
      setLocalPreview(null)
    } finally {
      cancelRef.current = null
      setProgress(null)
      onBusyChange?.(false)
    }
  }

  // Paste anywhere while the dialog is open.
  useEffect(() => {
    function onPaste(event: ClipboardEvent) {
      const file = Array.from(event.clipboardData?.files ?? [])[0]
      if (file) void accept(file)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const shown = preview ?? value ?? null
  const uploading = progress !== null

  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="label" style={{ margin: 0 }}>
        {label}
      </span>

      <div
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          void accept(event.dataTransfer.files?.[0])
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: 14,
          borderRadius: 'var(--radius-control)',
          border: `${dragging ? '2px' : '1px'} dashed ${
            dragging ? 'var(--accent)' : 'var(--hairline)'
          }`,
          background: dragging ? 'var(--accent-wash)' : 'var(--panel)',
          transition: 'background-color 0.15s var(--ease), border-color 0.15s var(--ease)',
        }}
      >
        {shown ? (
          <img
            src={shown}
            alt=""
            style={{
              width: 62,
              height: 88,
              objectFit: 'cover',
              borderRadius: 7,
              flex: 'none',
              border: '1px solid var(--hairline)',
              // Dim while the bytes are still going up, so a preview isn't
              // mistaken for a finished upload.
              opacity: uploading ? 0.55 : 1,
            }}
          />
        ) : (
          <div
            aria-hidden
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 62,
              height: 88,
              borderRadius: 7,
              flex: 'none',
              background: 'var(--panel-raised)',
              color: 'var(--text-tertiary)',
            }}
          >
            <Icon name="camera" size={20} />
          </div>
        )}

        <div className="stack grow" style={{ gap: 6, minWidth: 0 }}>
          {uploading ? (
            <>
              <span className="t-caption">Uploading… {Math.round((progress ?? 0) * 100)}%</span>
              <div
                role="progressbar"
                aria-valuenow={Math.round((progress ?? 0) * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{
                  height: 4,
                  borderRadius: 999,
                  background: 'var(--panel-raised)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.round((progress ?? 0) * 100)}%`,
                    height: '100%',
                    background: 'var(--accent)',
                    transition: 'width 0.15s linear',
                  }}
                />
              </div>
              <button
                type="button"
                className="btn btn-quiet"
                style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
                onClick={() => cancelRef.current?.()}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <span className="t-body">
                {shown ? 'Poster added.' : 'Drag an image here, or'}{' '}
                <button
                  type="button"
                  className="accent"
                  style={{ fontWeight: 600, textDecoration: 'underline' }}
                  onClick={() => inputRef.current?.click()}
                >
                  {shown ? 'replace it' : 'browse'}
                </button>
              </span>
              <span className="t-caption faint">{hint}</span>
              {shown && (
                <button
                  type="button"
                  className="btn btn-quiet"
                  style={{ alignSelf: 'flex-start', paddingLeft: 0, color: 'var(--destructive)' }}
                  onClick={() => {
                    setLocalPreview(null)
                    onChange(null)
                  }}
                >
                  Remove poster
                </button>
              )}
            </>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            void accept(event.target.files?.[0])
            // Reset so choosing the same file twice still fires a change.
            event.target.value = ''
          }}
        />
      </div>

      {error && (
        <p role="alert" className="t-caption" style={{ color: 'var(--destructive)' }}>
          {error}
        </p>
      )}
    </div>
  )
}
